import { css } from '@lastbrand/web-ui-styled-system/css'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

// Panda twin of legacy `productCTA.module.scss`. Off-grid sizes with no token equivalent are
// flagged inline as raw px (NO-TOKEN) so the escape hatch is visible.

// Wrapper around a CTA region. Legacy `.buttonWrapper { width: inherit }`.
export const buttonWrapperCss = css({
  width: 'inherit',
  lgDown: { width: 'full' },
})

export const buttonWrapperColumnCss = css({
  width: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  lgDown: { width: 'full' },
})

// Button `css` overrides are plain SystemStyleObjects (passed to the web-ui Button `css` prop,
// which does not accept `className`). Legacy `pointer-events-none` while a buy-now is mid-flight.
export const pointerEventsNoneCss: SystemStyleObject = { pointerEvents: 'none' }

// `className` form of the same `pointer-events-none` guard, for components that take a className
// string rather than a `css` prop (e.g. ExpressPay). Legacy applies `pointer-events-none` to the
// ExpressPay wrapper while a buy-now is mid-flight so Apple Pay can't trigger a second purchase.
export const pointerEventsNoneClassCss = css({ pointerEvents: 'none' })

/**
 * ExpressPay / mounted-button pattern. Legacy `.defaultCTAHidden` uses `visibility: hidden`
 * (NOT `display:none`) so the off-screen CTA keeps layout + remains measurable for ExpressPay
 * mounting. We preserve that exactly — collapsing height/margin/opacity but keeping the node mounted.
 */
export const defaultCTAHiddenCss = css({
  visibility: 'hidden',
  maxHeight: 0,
  margin: 0,
  opacity: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
})

// Error / OOS message. Legacy `.errorMessage` (13px, $umber red, centered).
export const errorMessageCss = css({
  textStyle: 'body-14',
  color: 'red-100',
  textAlign: 'center',
  marginBottom: 'space-16', // legacy 1.6rem
  lgDown: { marginBottom: 0 },
  '& a': { color: 'red-100' },
})

export const errorMessageLeftAlignCss = css({
  textAlign: 'left',
  lgDown: { textAlign: 'left' },
})

// Legacy `.errorMessage__quickATC` resets the centered alignment inside `breakpoint(mobileOnly)`
// (`text-align: unset`). Applied when the error renders in the mobile quick-ATC sticky footer.
export const errorMessageQuickATCCss = css({
  lgDown: { textAlign: 'unset' },
})

// Icon shown beside the V2 error message. Legacy `.errorMessage_newDesignV2 svg` is `display: none`
// on desktop and only `display: block` inside `breakpoint(mobileOnly)` — so the icon never shows on
// desktop, matching legacy. Render it in the DOM but gate visibility to `lgDown` only.
export const errorIconCss = css({
  display: 'none',
  lgDown: { display: 'block' },
  // NO-TOKEN: 20px icon box — `space-20` is a spacing token, not in the `sizes` scale, so it is
  // silently dropped on width/height. Use a literal px value to keep the icon sized.
  width: '20px',
  height: '20px',
  flexShrink: 0,
})

// Legacy `.errorMessage_newDesignV2` applies the flex row + left alignment + gap ONLY inside
// `breakpoint(mobileOnly)`; on desktop the V2 error message stays centered with the icon hidden.
// Gate the row layout to `lgDown` so desktop parity (centered, no visible icon) is preserved.
export const errorRowCss = css({
  lgDown: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space-8',
    textAlign: 'left',
  },
})

// In-stock-and-ready-to-ship message. Legacy `.inStockAndReadyToShipMessage` (13px, black, centered).
// Figma buy-box (`5992:6248`): the stock line sits `space-20` above the Add-to-bag button on mobile.
export const inStockMessageCss = css({
  textStyle: 'body-14',
  lineHeight: '16px', // legacy 1.6rem
  color: 'black',
  textAlign: 'center',
  marginTop: 0,
  marginBottom: 'space-16',
  lgDown: { marginBottom: 'space-20' },
})

export const inStockContentCss = css({
  lgDown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
})

// Holiday message row — legacy `.inStockAndReadyToShipMessage__holidayMessage` (icon + text inline).
export const holidayMessageRowCss = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lgDown: { justifyContent: 'flex-start' },
})

export const holidayIconCss = css({
  marginRight: 'space-6', // legacy 0.5rem (~5px); space-6 within 2px
  // NO-TOKEN: 20px holiday icon box — `space-20` is a spacing token, not in the `sizes` scale.
  width: '20px',
  height: '20px',
})

export const inStockIconCss = css({
  marginRight: 'space-8',
  // NO-TOKEN: 20px icon box — `space-20` is a spacing token, not in the `sizes` scale.
  width: '20px',
  height: '20px',
})

// Non-physical product / gift-card delivery text. Legacy `.inStockAndReadyToShipMessage` reuse.
export const deliveryTextCss = css({
  textStyle: 'body-14',
  lineHeight: '16px', // legacy 1.6rem
  color: 'black',
  textAlign: 'center',
  marginBottom: 'space-16',
})

// Waitlist email input. Legacy `.waitlistInput` (height 5rem === 50px, full-width, black border).
// Applied as `css.raw` to the web-ui InputTextField, which composes it with its own recipe.
export const waitlistInputCss = css.raw({
  width: 'full',
  maxWidth: 'full',
  marginTop: 0, // legacy .waitlistInput_newDesignV2 margin-top 0
  marginBottom: 'space-16', // legacy margin-bottom 1.6rem
})

// "Out of stock — back in stock after <date>" display text. Legacy `.displayTextWrapper`.
// Legacy `.displayTextWrapper` is `$umber`, and `__outOfStock` (the V2 / quick-ATC state) is `#af3535`
// red, centered. Use red-100 (the DS token for that red) — was incorrectly mediumgray.
// Legacy `oosText_newDesignV2`: desktop centered, no icon; mobile = left-aligned flex row, 8px gap, icon shown.
export const displayTextCss = css({
  width: 'inherit',
  textStyle: 'body-14',
  color: 'red-100',
  textAlign: 'center',
  paddingBottom: 'space-10',
  lgDown: { width: 'full', display: 'flex', alignItems: 'center', gap: 'space-8', textAlign: 'left' },
})

// Legacy `.displayTextWrapper__quickATC` resets the centered text on mobile (`text-align: unset`).
export const displayTextQuickATCCss = css({
  lgDown: { textAlign: 'unset' },
})

export const oosIconCss = css({
  display: 'none', // legacy svg: hidden on desktop, shown on mobile
  lgDown: { display: 'block' },
  // NO-TOKEN: 20px icon box — `space-20` is a spacing token, not in the `sizes` scale.
  width: '20px',
  height: '20px',
  flexShrink: 0,
})

// Leaving-soon "no longer available" copy above the Shop Similar button. Legacy `.leavingSoon__outOfStock`.
export const leavingSoonTextCss = css({
  textStyle: 'body-14',
  color: 'red-100',
  textAlign: 'center',
  marginTop: '28px', // legacy 2.8rem (no token)
  marginBottom: 'space-16',
  lgDown: { width: 'full', marginBottom: 'space-8' },
})

/**
 * "Waitlisted" / shop-similar button override. Legacy `.waitlisted` uses `$cantalope` (#FFA273), which is
 * the DS token `cantaloupe-100` exactly (#FFA273) — exact match, no delta.
 */
export const waitlistedButtonCss: SystemStyleObject = {
  backgroundColor: 'cantaloupe-100',
  color: 'black',
}

// Legacy `.waitlisted_newDesignV2` — V2 swaps the peach for a solid black button with white text.
export const waitlistedButtonNewDesignV2Css: SystemStyleObject = {
  backgroundColor: 'black',
  color: 'white',
}

// App-exclusive message. Legacy `.container__subDescText`: grey box — `background: $paper-white;
// padding: 2rem; margin: 0 0 2rem; font 1.3rem/1.9rem`. The bg + padding were missing, so it rendered
// as bare text on the DS path; restore them for parity with the legacy grey box.
export const appExclusiveMessageCss = css({
  textStyle: 'body-14',
  // Legacy `.container__subDescText` (incl. `_newDesignV2`) declares no color and inherits the
  // page default (`#21201f` = black) — it is neither `$umber` nor an explicit grey. `mediumgray`
  // was a wrong introduction; match the inherited black.
  color: 'black',
  backgroundColor: 'paperwhite',
  padding: 'space-20',
  marginTop: 'space-24', // legacy margin 2.4rem 0
  marginBottom: 'space-24',
})

export const appExclusiveLinkCss = css({
  color: 'darkgray',
  textDecoration: 'underline',
  display: 'inline',
  cursor: 'pointer',
  background: 'transparent',
  borderWidth: 0,
  padding: 0,
  font: 'inherit',
})

// Quick-ATC sticky wrapper (mobile fixed footer). Legacy `.quickATCWrapper`.
export const quickATCWrapperCss = css({
  lgDown: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: 'full',
    backgroundColor: 'white',
    paddingBlock: 'space-12',
    paddingInline: 'space-16',
    // legacy `border-top: 1px solid $gray85` (#D9D9D9 = lightgray); silvergray was too dark/heavy.
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'lightgray',
    zIndex: 10,
    textAlign: 'center',
  },
})

// Wrapper for the post-order "continue with this item" button (mobile centering).
export const postOrderCtaWrapperCss = css({
  lgDown: { width: 'full', textAlign: 'center' },
})

// ExpressPay row wrapper (desktop). Legacy `.defaultAtcWrapperForApplePay` / `.expressPayWrapperNoAnimation`
// — side-by-side ATC + Apple Pay. Gap is collapsed until Apple Pay reports ready (legacy gap 0 -> 1.2rem).
export const expressPayRowCss = css({
  display: 'flex',
  flexDirection: 'row-reverse',
  gap: 0,
  // v2 (the shipped path) is full-width.
  width: 'full',
})

export const expressPayRowReadyCss = css({ gap: 'space-12' })

// Klarna-suppressed spacing. Legacy `.productCTA_hideKlarna { padding-bottom: 1.4rem }`.
// NO-TOKEN: legacy 1.4rem (14px) is off the `space-*` grid — literal px keeps exact parity.
export const hideKlarnaCss = css({ paddingBottom: '14px' })

// --- GreyBoxes parity styles (legacy productForm.module.scss `.container__mtoText` / `.container__subDescText`) ---

// MTO / shipping-info grey box. Legacy `.container__mtoText` (paperwhite box, 13px, left-aligned).
export const mtoTextCss = css({
  width: 'inherit',
  textAlign: 'left',
  padding: 'space-20',
  marginBottom: 'space-20',
  backgroundColor: 'paperwhite',
  textStyle: 'body-14',
  '& p': { display: 'inline' },
  lgDown: {
    // NO-TOKEN: legacy mobile fixed width 31.5rem (315px).
    width: '315px',
    marginTop: 'space-40',
    marginBottom: 'space-16',
    paddingBlock: 'space-20',
    paddingInline: 'space-16',
  },
})

// Legacy `.container__mtoText_newDesignV2` overrides (full-width, larger copy).
// `!` mirrors legacy's `width: 100% !important` so it beats the base lgDown 315px on mobile.
export const mtoTextNewDesignV2Css = css({
  width: 'full!',
  marginTop: 'space-24',
  textStyle: 'body-14',
})

// Legacy `.container__mtoText_newDesignV2_hidden`.
export const mtoTextHiddenCss = css({ display: 'none' })

// Sub-description grey box. Legacy `.container__subDescText` + `_adjustedPadding`.
export const subDescTextCss = css({
  textAlign: 'left',
  padding: 'space-20',
  // longhands: a multi-value 'space-24 0' string does NOT resolve the token (silently dropped)
  marginBlock: 'space-24',
  marginInline: 0,
  backgroundColor: 'paperwhite',
  textStyle: 'body-14',
  '& p': { display: 'inline' },
  lgDown: {
    // NO-TOKEN: legacy mobile fixed width 31.5rem (315px).
    width: '315px',
    marginTop: 'space-40',
    marginBottom: 'space-16',
    paddingBlock: 'space-20',
    paddingInline: 'space-16',
  },
})

// Legacy `.container__subDescText_newDesignV2` (same full-width override as mtoText).
export const subDescTextNewDesignV2Css = css({ width: 'full!' })

// Legacy `.container__subDescText__subText` ($umber red) and `__textColorBlack` override.
export const subDescTextSubTextCss = css({ color: 'umber' })
export const subDescTextBlackCss = css({ color: 'black' })

// Cart-popup "View full product details" link. Legacy `.viewProductDetailsContainer` / `.viewProductDetailsContent`.
export const viewProductDetailsContainerCss = css({
  color: 'black',
  textStyle: 'body-14',
  textTransform: 'capitalize',
  marginBottom: 'space-24',
})

export const viewProductDetailsContentCss = css({
  paddingBottom: '0.5px',
  // NO-TOKEN: legacy 0.1rem (1px) bottom hairline.
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'black',
})
